/**
 * Pure arithmetic behind "Simulación de densidad y ambiente": yields for the recommended and
 * the grower's habitual density, under the estimated environment and under that environment
 * shifted up and down by the variability the user picks.
 *
 * Both densities stay fixed across the three environments — the question the simulator answers
 * is "if I sow at this density and the season comes out better or worse, what do I get?", so
 * only the curve moves, not the density read off it.
 */

export type CurvePoint = { density: number; yield: number };

export type SimulationRowKey = 'over' | 'estimated' | 'under';

export type SimulationRow = {
    key: SimulationRowKey;
    /** kg/ha the curve for this row was requested with. */
    environment: number;
    recommendedYield?: number;
    habitualYield?: number;
    /** Recommended minus habitual; undefined unless both yields are known. */
    densityDifferential?: number;
};

/**
 * Starting variability for a new recommendation, as `userBetterEnvironment` and
 * `userWorseEnvironment` on the crop config — the columns the simulator reads and writes.
 */
export const DEFAULT_ENVIRONMENT_VARIABILITY = 10;

/**
 * Environments for the three rows. Rounded because the curve endpoint takes an integer
 * environment, and rounding here keeps the row label and the request in agreement.
 *
 * The better and worse shifts are separate columns (the legacy calculator set them apart); the
 * new wizard's single slider writes the same value to both, so `worsePercent` defaults to it.
 */
export const environmentsFor = (environment: number, betterPercent: number, worsePercent = betterPercent) => ({
    over: Math.round(environment * (1 + betterPercent / 100)),
    estimated: environment,
    under: Math.round(environment * (1 - worsePercent / 100)),
});

/**
 * Yield at `density`, linearly interpolated between the two curve points around it. The curve
 * comes on a 500 pl/ha grid, so a density typed by hand rarely lands on a point. Undefined
 * outside the curve's range: extrapolating a yield curve past its ends isn't a number to quote.
 */
export const yieldAtDensity = (points: CurvePoint[] | undefined, density: number | undefined) => {
    if (!points?.length || density === undefined || !Number.isFinite(density)) return undefined;
    const sorted = points.slice().sort((a, b) => a.density - b.density);
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    if (!first || !last || density < first.density || density > last.density) return undefined;

    const upperIndex = sorted.findIndex((point) => point.density >= density);
    const upper = sorted[upperIndex];
    const lower = sorted[upperIndex - 1];
    if (!upper) return undefined;
    if (upper.density === density || !lower) return upper.yield;

    const ratio = (density - lower.density) / (upper.density - lower.density);
    return lower.yield + ratio * (upper.yield - lower.yield);
};

const difference = (a?: number, b?: number) => (a !== undefined && b !== undefined ? a - b : undefined);

export const buildSimulationRows = ({
    environments,
    curves,
    recommendedDensity,
    recommendedYield,
    habitualDensity,
}: {
    environments: ReturnType<typeof environmentsFor>;
    curves: Partial<Record<SimulationRowKey, CurvePoint[]>>;
    recommendedDensity?: number;
    /**
     * The endpoint's own yield at the recommended density for the estimated environment. Used
     * for that cell instead of interpolating, so it matches the hybrid card to the kilo.
     */
    recommendedYield?: number;
    habitualDensity?: number;
}): SimulationRow[] =>
    (['over', 'estimated', 'under'] as const).map((key) => {
        const points = curves[key];
        const recommended =
            key === 'estimated' && recommendedYield !== undefined
                ? recommendedYield
                : yieldAtDensity(points, recommendedDensity);
        const habitual = yieldAtDensity(points, habitualDensity);
        return {
            key,
            environment: environments[key],
            recommendedYield: recommended,
            habitualYield: habitual,
            densityDifferential: difference(recommended, habitual),
        };
    });

/**
 * "Diferencia por ambiente": how much a column gains in the better environment and loses in
 * the worse one, both against the estimated row.
 */
export const environmentDifferences = (rows: SimulationRow[], column: 'recommendedYield' | 'habitualYield') => {
    const byKey = Object.fromEntries(rows.map((row) => [row.key, row[column]])) as Partial<
        Record<SimulationRowKey, number>
    >;
    return {
        up: difference(byKey.over, byKey.estimated),
        down: difference(byKey.under, byKey.estimated),
    };
};

/**
 * Everything the "Rendimientos según densidad y ambiente" table needs, already computed.
 *
 * The table renders in two places: on the page, and server-side for its copy-as-image button.
 * The server-side render can't fetch, and the inputs here (habitual density, variability, the
 * shifted-environment curves) only live in the browser, so the page sends this whole snapshot
 * in the block-image payload rather than the render re-deriving it from the saved blob.
 */
export type DensitySimulationSnapshot = {
    hybridName: string;
    /** Plants/ha. */
    recommendedDensity?: number;
    /** Plants/ha; undefined while the user hasn't typed one (or typed one off the curve). */
    habitualDensity?: number;
    /** How far above and below the estimated environment the other two rows sit, in %. */
    betterPercent: number;
    worsePercent: number;
    rows: SimulationRow[];
    recommendedDifferences: ReturnType<typeof environmentDifferences>;
    habitualDifferences: ReturnType<typeof environmentDifferences>;
};

/**
 * The table's snapshot for one hybrid, from its three curves. Shared by the live views (wizard,
 * result page) and the PDF adapter, which reads the same curves out of the saved backup — so
 * both quote the same yields.
 */
export const toSimulationSnapshot = ({
    hybridName,
    environment,
    betterPercent,
    worsePercent = betterPercent,
    recommendedDensity,
    recommendedYield,
    habitualDensity,
    curves,
}: {
    hybridName: string;
    environment?: number;
    betterPercent: number;
    worsePercent?: number;
    recommendedDensity?: number;
    recommendedYield?: number;
    habitualDensity?: number;
    curves: Partial<Record<SimulationRowKey, CurvePoint[]>>;
}): DensitySimulationSnapshot | undefined => {
    if (!environment) return undefined;
    const rows = buildSimulationRows({
        environments: environmentsFor(environment, betterPercent, worsePercent),
        curves,
        recommendedDensity,
        recommendedYield,
        habitualDensity,
    });
    return {
        hybridName,
        recommendedDensity,
        habitualDensity,
        betterPercent,
        worsePercent,
        rows,
        recommendedDifferences: environmentDifferences(rows, 'recommendedYield'),
        habitualDifferences: environmentDifferences(rows, 'habitualYield'),
    };
};
